import { globalFetch } from 'foundation/js/api'

const toDashboardBooleanFlag = (value) => {
    if (typeof value === 'boolean') {
        return value
    }
    if (typeof value === 'number') {
        return value === 1
    }
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase()
        return normalized === 'true' || normalized === '1'
    }
    return false
}

export default {
    namespaced: true,
    state: {
        fundraiseCreationCompleteShowReceipt: false,
        isFundraiseImageUploading: false,
        personalStoryImagesUploading: 0,
        personalDonationShowReceipt: false,
        fundraiseCreationEventId: null,
        donation_settings: {
            donationId: undefined,
            linkToInformationHowDonationBenefits: undefined,
            single: {
                presets: [],
                defaultPreset: undefined
            },
            recurring: {
                presets: [],
                defaultPreset: undefined
            }
        },
        isSubmitting: false,
        isEditing: true,
        isFundraiseImageUploadError: false,
        isPersonalStoryImageUploadError: false,
        personalFundraisingFlowStep: null,
        userProfileFundraisingCampaigns: [],
        dashboardCollections: [],
        dashboardTotalAmount: null,
        dashboardIsMindegave: null,
        dashboardExpiredHasNextPage: false,
        dashboardExpiredPage: 1,
        dashboardCollectionsLoading: false,
        dashboardCollectionsError: null
    },
    mutations: {
        completeFundraiseCreation(state) {
            state.fundraiseCreationCompleteShowReceipt = true
        },
        completePersonalDonation(state) {
            state.personalDonationShowReceipt = true
        },
        set_fundraiseCreationEventId(state, eventId) {
            state.fundraiseCreationEventId = eventId
        },
        set_donation_settings(state, settings) {
            state.donation_settings = settings
        },
        set_submitting(state, isSubmitting) {
            state.isSubmitting = isSubmitting
        },
        set_fundraise_image_uploading(state, isUploading) {
            state.isFundraiseImageUploading = isUploading
        },
        add_personal_story_image_uploading(state) {
            state.personalStoryImagesUploading += 1
        },
        remove_personal_story_image_uploading(state) {
            state.personalStoryImagesUploading -= 1
        },
        set_fundraise_image_upload_error(state, data) {
            state.isFundraiseImageUploadError = data
        },
        set_personal_story_image_upload_error(state, data) {
            state.isPersonalStoryImageUploadError = data
        },
        set_editing(state, isEditing) {
            state.isEditing = isEditing
        },
        set_user_profile_fundraising_campaigns(state, data) {
            state.userProfileFundraisingCampaigns = data
        },
        set_personal_fundraising_flow_step(state, step) {
            state.personalFundraisingFlowStep = step
        },
        set_dashboard_collections(state, data) {
            state.dashboardCollections = data
        },
        append_dashboard_expired_collections(state, data) {
            const existingIds = new Set(
                state.dashboardCollections.map((item) => String(item.id))
            )
            const nextExpired = data.filter(
                (item) => !existingIds.has(String(item.id))
            )
            state.dashboardCollections = [
                ...state.dashboardCollections,
                ...nextExpired
            ]
        },
        set_dashboard_total_amount(state, amount) {
            state.dashboardTotalAmount = amount
        },
        set_dashboard_is_mindegave(state, isMindegave) {
            state.dashboardIsMindegave =
                isMindegave == null ? null : Boolean(isMindegave)
        },
        set_dashboard_expired_meta(state, { page, hasNextPage }) {
            state.dashboardExpiredPage = page
            state.dashboardExpiredHasNextPage = hasNextPage
        },
        set_dashboard_collections_loading(state, isLoading) {
            state.dashboardCollectionsLoading = isLoading
        },
        set_dashboard_collections_error(state, error) {
            state.dashboardCollectionsError = error
        }
    },
    actions: {
        getPersonalFundraisingDashboard(context, payload) {
            const {
                userProfileId,
                campaignAreaId,
                pageId,
                expiredPage = 1,
                expiredPageSize = 5,
                appendExpired = false
            } = typeof payload === 'string' || typeof payload === 'number'
                ? { userProfileId: payload }
                : payload || {}

            if (
                !userProfileId ||
                !process.env.GET_PERSONAL_FUNDRAISING_DASHBOARD
            ) {
                return Promise.resolve([])
            }

            if (!campaignAreaId && !pageId) {
                return Promise.resolve([])
            }

            const params = new URLSearchParams({
                expiredPage: String(expiredPage),
                expiredPageSize: String(expiredPageSize)
            })
            if (campaignAreaId) {
                params.set('campaignArea', campaignAreaId)
            }
            if (pageId) {
                params.set('pageId', String(pageId))
            }

            const url = `${document.location.origin}/${process.env.GET_PERSONAL_FUNDRAISING_DASHBOARD.replace('[userProfileId]', userProfileId)}?${params.toString()}`

            if (!appendExpired) {
                context.commit('set_dashboard_collections_loading', true)
                context.commit('set_dashboard_collections_error', null)
            }

            return globalFetch(url)
                .then((response) => {
                    if (!response.ok) {
                        throw new Error(`Server returned status ${response.status}`)
                    }
                    return response.json()
                })
                .then((data) => {
                    const rawItems = Array.isArray(data)
                        ? data
                        : Array.isArray(data?.collections)
                          ? data.collections
                          : []

                    const collections = rawItems
                        .map((item) => ({
                            id: item.id,
                            name: item.name,
                            url: item.url || null,
                            editUrl: item.editUrl || null,
                            qrCodeUrl: item.qrCodeUrl || null,
                            qrCodeImage: item.qrCodeImage || null,
                            livestreamUrl: item.livestreamUrl || null,
                            amount: Number(item.amount) || 0,
                            targetAmount: Number(item.targetAmount) || 0,
                            isActive: toDashboardBooleanFlag(item.isActive),
                            endDate: item.endDate || null,
                            donorCount: Number(item.donorCount) || 0
                        }))
                        .filter((item) => item.id != null && item.name)

                    if (appendExpired) {
                        const expiredOnly = collections.filter((item) => !item.isActive)
                        context.commit(
                            'append_dashboard_expired_collections',
                            expiredOnly
                        )
                    } else {
                        context.commit('set_dashboard_collections', collections)
                        if (data?.totalAmount != null && data?.totalAmount !== '') {
                            context.commit(
                                'set_dashboard_total_amount',
                                Number(data.totalAmount) || 0
                            )
                        } else {
                            context.commit('set_dashboard_total_amount', null)
                        }
                        if (typeof data?.isMindegave === 'boolean') {
                            context.commit(
                                'set_dashboard_is_mindegave',
                                data.isMindegave
                            )
                        }
                    }

                    context.commit('set_dashboard_expired_meta', {
                        page: Number(data?.expiredPage) || expiredPage,
                        hasNextPage: Boolean(data?.expiredHasNextPage)
                    })

                    return collections
                })
                .catch((err) => {
                    console.error(err)
                    if (!appendExpired) {
                        context.commit('set_dashboard_collections_error', err)
                        context.commit('set_dashboard_collections', [])
                        context.commit('set_dashboard_total_amount', null)
                        context.commit('set_dashboard_is_mindegave', null)
                        context.commit('set_dashboard_expired_meta', {
                            page: 1,
                            hasNextPage: false
                        })
                    }
                    return []
                })
                .finally(() => {
                    if (!appendExpired) {
                        context.commit('set_dashboard_collections_loading', false)
                    }
                })
        },
        loadMoreExpiredDashboardCollections(context, payload) {
            const { userProfileId, campaignAreaId, pageId } = payload || {}
            if (
                !userProfileId ||
                (!campaignAreaId && !pageId) ||
                !context.state.dashboardExpiredHasNextPage
            ) {
                return Promise.resolve([])
            }

            return context.dispatch('getPersonalFundraisingDashboard', {
                userProfileId,
                campaignAreaId,
                pageId,
                expiredPage: context.state.dashboardExpiredPage + 1,
                expiredPageSize: 5,
                appendExpired: true
            })
        },
        getUserProfileFundraisingCampaigns(context, userProfileId) {
            const url = `${document.location.origin}/${process.env.GET_USERPROFILE_FUNDRAISING_CAMPAIGNS.replace('[userProfileId]', userProfileId)}`
            globalFetch(url)
                .then((response) => response.json())
                .then((data) => {
                    const campaigns = Array.isArray(data)
                        ? data.filter(
                              (campaign) => campaign.name && campaign.url
                          )
                        : []
                    context.commit(
                        'set_user_profile_fundraising_campaigns',
                        campaigns
                    )
                })
                .catch((err) => {
                    console.error(err)
                })
        },
        setEditing(context, isEditing) {
            context.commit('set_editing', isEditing)
        },
        discardUploadedImage(context, payload) {
            const url = process.env.DISCARDFUNDRAISEIMAGE
            const method = process.env.FETCHMETHOD

            const headers = new Headers()
            headers.append('Content-Type', 'application/json')
            const init = { headers, method, body: JSON.stringify(payload) }

            if (method !== 'POST') delete init.body // leave this in

            return globalFetch(url, init)
                .then((response) => {
                    if (response.ok) {
                        return response.json()
                    }
                    throw 'File deletion failed'
                })
                .catch((err) =>
                    console.error(
                        'Der skete en fejl med sletning af billedet',
                        err
                    )
                )
        },
        uploadFundraiseImage(context, payload) {
            const url = `${document.location.origin}/${process.env.UPLOAD_FUNDRAISEIMAGES.replace('[id]', payload.id)}`
            const method = process.env.POSTMETHOD

            const data = new FormData()
            data.append('imageFile', payload.file)

            //Explicitly excluded 'Content-Type' to allow the browser to
            //calculate the 'multipart/form-data' boundary
            const headers = new Headers()
            const init = { headers, method, body: data }

            if (method !== 'POST') delete init.body // leave this in

            if (payload.type == 'fundraiseImage')
                context.commit('set_fundraise_image_uploading', true)
            if (payload.type == 'personalStoryImage')
                context.commit('add_personal_story_image_uploading')

            return globalFetch(url, init)
                .then((response) => {
                    if (response.ok) {
                        return response.json()
                    }
                    throw ('Response not ok', response)
                })
                .catch((err) => {
                    console.error('Der skete en fejl med dit billedupload', err)
                    if (payload.type == 'fundraiseImage')
                        context.commit('set_fundraise_image_upload_error', true)
                    if (payload.type == 'personalStoryImage')
                        context.commit(
                            'set_personal_story_image_upload_error',
                            true
                        )
                })
                .finally(() => {
                    if (payload.type == 'fundraiseImage')
                        context.commit('set_fundraise_image_uploading', false)
                    if (payload.type == 'personalStoryImage')
                        context.commit('remove_personal_story_image_uploading')
                })
        },
        createPersonalfundraising(context, payload) {
            const url = `${document.location.origin}/${process.env.CREATE_PERSONALFUNDRAISING.replace('[id]', payload.id)}`
            const method = process.env.POSTMETHOD

            const headers = new Headers()
            headers.append('Content-Type', 'application/json')
            const init = {
                headers,
                method,
                body: JSON.stringify(payload.content)
            }

            if (method !== 'POST') delete init.body // leave this in

            context.commit('set_submitting', true)
            globalFetch(url, init)
                .then((response) => {
                    if (response.ok) return response.json()
                    throw new Error(response.error)
                })
                .then((json) => {
                    context.commit('completeFundraiseCreation')
                    context.commit('set_editing', false)
                    document.location.href = json.continueUrl
                })
                .catch((err) => {
                    console.error('Der skete en fejl med din indsamling', err)
                })
                .finally(() => {
                    context.commit('set_submitting', false)
                })
        },
        updatePersonalfundraising(context, { id, content }) {
            const url = `${document.location.origin}/${process.env.UPDATE_PERSONALFUNDRAISING.replace('[id]', id)}`
            const method = process.env.PUTMETHOD

            const headers = new Headers()
            headers.append('Content-Type', 'application/json')
            const init = { headers, method, body: JSON.stringify(content) }

            if (method !== 'PUT') delete init.body // leave this in

            context.commit('set_submitting', true)
            globalFetch(url, init)
                .then((response) => {
                    if (response.ok) return response.json()
                    context.commit('set_submitting', false)
                    throw new Error(response.error)
                })
                .then((json) => {
                    context.commit('completeFundraiseCreation')
                    context.commit('set_editing', false)
                    document.location.href = json.continueUrl
                })
                .catch((err) => {
                    context.commit('set_submitting', false)
                    console.error('Der skete en fejl med din indsamling', err)
                })
        },
        deletePersonalfundraising(context, payload) {
            const url =
                `${document.location.origin}/${process.env.DELETE_PERSONALFUNDRAISING.replace('[id]', payload.id)}` +
                '?pageId=' +
                payload.pageId
            const method = process.env.DELETEMETHOD

            const headers = new Headers()
            const init = { headers, method }

            context.commit('set_submitting', true)
            globalFetch(url, init)
                .then((response) => {
                    if (response.ok) return response.json()
                    context.commit('set_submitting', false)
                    throw new Error(response.error)
                })
                .then((json) => {
                    context.commit('set_editing', false)
                    document.location.href = json.continueUrl
                })
                .catch((err) => {
                    context.commit('set_submitting', false)
                    console.error('Der skete en fejl med din indsamling', err)
                })
        },
        // FF1 Støt os
        submitPersonalDonation(context, payload) {
            const url =
                `${document.location.origin}/${process.env.SUBMIT_DONATION}`.replace(
                    '[pageId]',
                    payload.pageId
                )
            const method = process.env.POSTMETHOD

            const headers = new Headers()
            headers.append('Content-Type', 'application/json')
            const init = { headers, method, body: JSON.stringify(payload) }

            if (method !== 'POST') delete init.body // leave this in

            const isPbs = payload.paymentMethod == '4'

            context.commit('set_submitting', true)

            globalFetch(url, init)
                .then((response) => {
                    if (response.ok) return response.json()
                    context.commit('set_submitting', false)
                    throw new Error(response.error)
                })
                .then((json) => {
                    if (isPbs) {
                        context.commit('set_editing', false)
                        context.commit('completePersonalDonation')
                    } else {
                        context.commit('set_editing', false)
                        document.location.href = json.paymentUrl
                    }
                })
                .catch((err) => {
                    context.commit('set_submitting', false)
                    console.error(
                        'Der skete desværre en fejl med din donation',
                        err
                    )
                })
        },
        // FF3 Donation til digitale indsamlinger
        submitPersonalDonation2(context, { fundraisingCampaignId, payload }) {
            let url = `${document.location.origin}/${process.env.SUBMIT_PERSONAL_DONATION}`
            url = url.replace('[guid]', fundraisingCampaignId)

            const method = process.env.POSTMETHOD

            const headers = new Headers()
            headers.append('Content-Type', 'application/json')
            const init = { headers, method, body: JSON.stringify(payload) }

            if (method !== 'POST') delete init.body // leave this in

            context.commit('set_submitting', true)
            return globalFetch(url, init)
                .then((response) => {
                    if (!response.ok) {
                        throw new Error('Donation submit failed')
                    }
                    return response.json()
                })
                .then((json) => {
                    if (!json?.continueUrl) {
                        throw new Error('Missing continueUrl from donation API')
                    }
                    context.commit('set_editing', false)
                    document.location.href = json.continueUrl
                    return true
                })
                .catch((err) => {
                    context.commit('set_submitting', false)
                    console.error('Der skete en fejl med din donation', err)
                    return false
                })
        },
        // FF3 MobilePay overlay donation (no email required)
        submitPersonalMobilePayDonation(context, { fundraisingCampaignId, payload }) {
            let url = `${document.location.origin}/${process.env.SUBMIT_PERSONAL_MOBILEPAY_DONATION}`
            url = url.replace('[guid]', fundraisingCampaignId)

            const method = process.env.POSTMETHOD

            const headers = new Headers()
            headers.append('Content-Type', 'application/json')
            const init = { headers, method, body: JSON.stringify(payload) }

            if (method !== 'POST') delete init.body // leave this in

            context.commit('set_submitting', true)
            return globalFetch(url, init)
                .then((response) => {
                    if (!response.ok) {
                        throw new Error('MobilePay donation submit failed')
                    }
                    return response.json()
                })
                .then((json) => {
                    if (!json?.continueUrl) {
                        throw new Error('Missing continueUrl from MobilePay donation API')
                    }
                    context.commit('set_editing', false)
                    document.location.href = json.continueUrl
                    return true
                })
                .catch((err) => {
                    context.commit('set_submitting', false)
                    console.error('Der skete en fejl med din MobilePay donation', err)
                    return false
                })
        },
        async GetHighlightedDonations({ commit }, { fundraisingCampaignId }) {
            let url = `${document.location.origin}/${process.env.GET_DONATIONS}`
            url = url.replace('[campaignAreaId]', fundraisingCampaignId)

            const init = {
                method: process.env.GETMETHOD,
                headers: new Headers({ 'Content-Type': 'application/json' })
            }

            try {
                commit('set_submitting', true)

                const response = await globalFetch(url, init)
                if (!response.ok) {
                    throw new Error(`Server returned status ${response.status}`)
                }

                const raw = await response.json()
                const sum = typeof raw === 'number' ? raw : Number(raw)

                return sum
            } catch (err) {
                console.error(
                    'Failed to fetch highlighted donations:',
                    err
                )
                throw err
            } finally {
                commit('set_submitting', false)
            }
        },

        showReceipt(context, payload) {
            context.commit('completePersonalDonation')
        }
    },
    getters: {}
}
